import React, { useRef, useState } from 'react';
import { 
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const HotFileUpload = ({
  maxSize=1024 * 1024 * 6,
  acceptedFileTypes={
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "application/pdf": [".pdf"],
  },
  multiple = false,
  onChange,
  placeholder = 'Drag and drop files here, or click to select files'
}) => {
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const inputRef = useRef(null);

  const validateFiles = (fileList) => {
    const invalidFiles = [];
    const validFiles = [];

    Array.from(fileList).forEach(file => {
      if (file.size > maxSize) {
        invalidFiles.push(`הקובץ ${file.name} גדול מידי. המקסימום גודל הקובץ הוא ${maxSize / 1024 / 1024}MB`);
      } else if (acceptedFileTypes && !Object.entries(acceptedFileTypes).some(([type, exts]) => {
        return file.type.match(type) || exts?.some(ext => file.name.toLowerCase().endsWith(ext.toLowerCase()));
      })) {
        invalidFiles.push(`סוג הקובץ אינו נתמך ${file.name}`);
      } else {
        validFiles.push(file);
      }
    });

    return { validFiles, invalidFiles };
  };

  const handleFiles = (fileList) => {
    const { validFiles, invalidFiles } = validateFiles(fileList);

    if (invalidFiles.length > 0) {
      setError(invalidFiles.join(', '));
      return;
    }

    setError(null);
    const newFiles = multiple ? [...files, ...validFiles] : validFiles;
    setFiles(newFiles);
    onChange?.(newFiles);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const droppedFiles = e.dataTransfer.files;
    handleFiles(droppedFiles);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleInputChange = (e) => {
    const selectedFiles = e.target.files;
    handleFiles(selectedFiles);
  };

  const removeFile = (fileToRemove) => {
    const updatedFiles = files.filter(file => file !== fileToRemove);
    setFiles(updatedFiles);
    onChange?.(updatedFiles);
  };

  return (
    <Box>
      {multiple || files.length == 0 && (
        <Paper
        elevation={0}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragActive ? 'grey.50' : 'background.paper',
          '&:hover': {
            borderColor: 'primary.main',
          },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={acceptedFileTypes ? Object.entries(acceptedFileTypes)
            .flatMap(([type, exts]) => [type, ...(exts || [])])
            .join(',') : undefined}
          onChange={handleInputChange}
          style={{ display: 'none' }}
        />
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography>{placeholder}</Typography>
      </Paper>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      
      {files.length > 0 && (
        <List sx={{ mt: 2 }}>
          {files.map((file, index) => (
            <ListItem
              key={`${file.name}-${index}`}
              secondaryAction={
                <IconButton edge="end" onClick={() => removeFile(file)} color="error">
                  <DeleteIcon />
                </IconButton>
              }
              sx={{
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 1,
                mb: 1,
              }}
            >
              <ListItemText 
                primary={file.name}
                secondary={`${(file.size / 1024).toFixed(2)} KB`}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default HotFileUpload; 